import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Search,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/common/Logo';
import { Avatar } from '@/components/ui/Avatar';
import { FOOTER_NAV, NAV, type NavItem } from './navItems';
import { useCurrentUser, useSession } from '@/features/auth/session';
import {
  DropdownMenu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
} from '@/components/ui/DropdownMenu';

const STORAGE_KEY = 'kiwasco_sidebar_collapsed';

export function Sidebar() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(STORAGE_KEY) === '1';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  return (
    <aside
      className={cn(
        'flex flex-col h-screen sticky top-0 shrink-0 bg-white border-r border-line',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-[72px]' : 'w-[264px]',
      )}
    >
      {/* Brand row */}
      <div
        className={cn(
          'flex items-center gap-2 px-4 h-14 border-b border-line',
          collapsed && 'justify-center px-0',
        )}
      >
        <Logo variant="mark" className="w-7 h-9" />
        {!collapsed && (
          <div className="text-body-strong leading-tight text-navy-deep">
            KIWASCO
            <div className="text-caption font-normal text-ink-muted -mt-0.5">
              Back Office
            </div>
          </div>
        )}
      </div>

      {/* Quick search */}
      {!collapsed && (
        <div className="px-3 pt-3 pb-1">
          <button
            type="button"
            className={cn(
              'w-full inline-flex items-center gap-2 h-9 px-2.5 rounded-lg',
              'border border-line bg-canvas text-caption text-ink-muted',
              'hover:bg-white hover:border-line transition-colors',
            )}
          >
            <Search className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="text-[10px] font-mono px-1 rounded bg-white border border-line text-ink-muted">
              ⌘K
            </kbd>
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-2">
        <ul className="flex flex-col gap-0.5">
          {NAV.map((item) => (
            <NavRow key={item.to} item={item} collapsed={collapsed} />
          ))}
        </ul>
      </nav>

      {/* Footer nav */}
      <div className="px-2 py-2 border-t border-line">
        <ul className="flex flex-col gap-0.5 mb-2">
          {FOOTER_NAV.map((item) => (
            <NavRow key={item.to} item={item} collapsed={collapsed} />
          ))}
        </ul>
        <SidebarProfile collapsed={collapsed} />
      </div>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className={cn(
          'h-9 mx-3 mb-3 rounded-md border border-line bg-white',
          'flex items-center justify-center gap-1.5 text-caption text-ink-muted',
          'hover:bg-canvas transition-colors',
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <>
            <ChevronLeft className="h-4 w-4" />
            <span>Collapse</span>
          </>
        )}
      </button>
    </aside>
  );
}

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const hasChildren = (item.children?.length ?? 0) > 0;
  const isActive = hasChildren
    ? location.pathname.startsWith(item.to)
    : location.pathname === item.to;
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  if (hasChildren && !collapsed) {
    return (
      <li>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'group w-full flex items-center gap-2.5 h-10 px-3 rounded-md text-body',
            'hover:bg-canvas transition-colors',
            isActive && 'text-navy',
          )}
        >
          <item.icon className="h-[18px] w-[18px] shrink-0" />
          <span className="flex-1 text-left">{item.label}</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-ink-muted transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
        {open && (
          <ul className="mt-0.5 mb-1 pl-3 ml-[22px] border-l border-line">
            {item.children?.map((c) => (
              <li key={c.to}>
                <SimpleRow item={c} collapsed={false} childRow />
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li>
      <SimpleRow item={item} collapsed={collapsed} />
    </li>
  );
}

function SimpleRow({
  item,
  collapsed,
  childRow = false,
}: {
  item: NavItem;
  collapsed: boolean;
  childRow?: boolean;
}) {
  const location = useLocation();
  return (
    <NavLink
      to={item.to}
      end={item.to === '/requests'}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) => {
        // For nested items like /requests/exhauster vs /requests, prefer exact match.
        const active =
          item.to === '/requests'
            ? location.pathname === '/requests'
            : isActive;
        return cn(
          'relative flex items-center gap-2.5 h-10 px-3 rounded-md text-body',
          'transition-colors hover:bg-canvas',
          active && 'bg-status-info-bg text-brand-blue font-semibold',
          collapsed && 'justify-center px-0',
          childRow && 'h-8 text-caption',
        );
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && !childRow && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-navy-deep" />
          )}
          <item.icon
            className={cn(
              'shrink-0',
              childRow ? 'h-[15px] w-[15px]' : 'h-[18px] w-[18px]',
            )}
          />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

function SidebarProfile({ collapsed }: { collapsed: boolean }) {
  const user = useCurrentUser();
  const signOut = useSession((s) => s.signOut);
  const name = [user?.usrFirstName, user?.usrLastName]
    .filter(Boolean)
    .join(' ') || user?.usrMobileNumber || 'Back office';
  const role = (user?.usrRoleName as string | undefined) ?? 'Reviewer';

  return (
    <DropdownMenu
      align="end"
      trigger={
        <button
          type="button"
          className={cn(
            'w-full flex items-center gap-2.5 p-2 rounded-md',
            'hover:bg-canvas transition-colors text-left',
            collapsed && 'justify-center',
          )}
        >
          <Avatar name={name} size="sm" />
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-body-strong text-ink truncate">{name}</div>
                <div className="text-caption text-ink-muted truncate">{role}</div>
              </div>
              <ChevronDown className="h-4 w-4 text-ink-muted shrink-0" />
            </>
          )}
        </button>
      }
    >
      <MenuLabel>Signed in as</MenuLabel>
      <div className="px-2.5 pb-2 text-caption text-ink-muted truncate">{name}</div>
      <MenuSeparator />
      <MenuItem danger onSelect={signOut} iconLeft={<LogOut className="h-4 w-4" />}>
        Sign out
      </MenuItem>
    </DropdownMenu>
  );
}
